/**
 * The Museum (docs/fossil-eras-plan.md R3): what fossils buy.
 *
 * Fossils held raise production by 5% each, and a perk spends them — so
 * every purchase costs the bonus as well as the price, and choosing is the
 * point. Most perks change how the *next* era begins rather than doing
 * anything now, which is what keeps them different in kind from the shard
 * shop's tools.
 */

export interface PerkDef {
  id: string;
  name: string;
  description: string;
  /** Fossils. Spent ones stop counting toward the production bonus. */
  cost: number;
  /** Id of the perk that must be owned first, if any. */
  requires?: string;
}

export const PERKS: readonly PerkDef[] = [
  {
    id: 'museum:hatchery',
    name: 'Hatchery',
    description:
      'Every era starts with a Compsognathus, a Gallimimus and an Oviraptor already hatched.',
    cost: 3,
  },
  {
    id: 'museum:old-bones',
    name: 'Old Bones',
    description: 'Every era starts with 1,000 eggs for each fossil you hold.',
    cost: 5,
  },
  {
    id: 'museum:night-sky',
    name: 'Night Sky',
    description: 'Away rate +10% in every era: your dinosaurs lay more while the game is closed.',
    cost: 8,
  },
  {
    id: 'museum:prospector',
    name: 'Prospector',
    description: 'Meteor shards left when an era ends become fossils, 5,000 to one.',
    cost: 10,
  },
  {
    id: 'museum:toolbox',
    name: 'Toolbox',
    description: 'Every era starts with the whole pick chain, up to the Diamond Drill.',
    cost: 12,
    requires: 'museum:hatchery',
  },
  {
    id: 'museum:warm-blood',
    name: 'Warm Blood',
    description: 'Click upgrades survive an extinction.',
    cost: 15,
  },
  {
    id: 'museum:long-memory',
    name: 'Long Memory',
    description: 'The doublers on your first four dinosaurs survive an extinction.',
    cost: 20,
    requires: 'museum:warm-blood',
  },
  {
    id: 'museum:deep-time',
    name: 'Deep Time',
    description: 'Time with the game closed counts for up to 24 hours instead of 8.',
    cost: 25,
  },
  {
    id: 'museum:falling-stars',
    name: 'Falling Stars',
    description: 'Meteors and golden eggs fall twice as often for the first hour of every era.',
    cost: 30,
    requires: 'museum:night-sky',
  },
];

export const PERKS_BY_ID: ReadonlyMap<string, PerkDef> = new Map(PERKS.map((p) => [p.id, p]));

/** Eggs each held fossil is worth at the start of an era, with Old Bones. */
export const OLD_BONES_EGGS_PER_FOSSIL = 1000;
/** Shards per fossil when Prospector converts what an era did not spend. */
export const PROSPECTOR_SHARDS_PER_FOSSIL = 5000;
/** Offline points Night Sky adds, in the same units as the shard shop's nights. */
export const NIGHT_SKY_OFFLINE = 0.1;
/** Seconds of offline progress Deep Time raises the cap to. */
export const DEEP_TIME_CAP_SECONDS = 24 * 60 * 60;
/** The dinosaurs Hatchery hatches at the start of every era. */
export const HATCHERY_DINOS: readonly string[] = ['compsognathus', 'gallimimus', 'oviraptor'];
/** The dinosaurs whose doublers Long Memory keeps. */
export const LONG_MEMORY_DINOS: readonly string[] = [
  'compsognathus',
  'gallimimus',
  'oviraptor',
  'triceratops',
];
/** How long Falling Stars runs for at the start of an era, in seconds. */
export const FALLING_STARS_SECONDS = 60 * 60;
